import { motion, useInView } from 'framer-motion';
import { useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Settings2,
  Server,
  Terminal,
  Rocket,
  Sparkles,
  CheckCircle2,
  Clock,
  Globe,
  Key,
  Cloud,
  HardDrive
} from 'lucide-react';

const getWorkflowCards = (t: any) => [
  {
    id: 'configure',
    icon: Settings2,
    title: t('landing.workflow.configure.title'),
    subtitle: t('landing.workflow.configure.subtitle'),
    color: 'from-blue-500 to-blue-600',
    bgColor: 'bg-blue-500/5 dark:bg-blue-500/10',
    borderColor: 'border-blue-200/50 dark:border-blue-800/30',
    details: [
      t('landing.workflow.configure.aiModel'),
      t('landing.workflow.configure.hostingType'),
      t('landing.workflow.configure.region')
    ],
    options: [
      {
        icon: Key,
        label: t('landing.workflow.configure.platformApiKey'),
        subtext: t('landing.workflow.configure.platformApiKeySubtext')
      },
      {
        icon: Key,
        label: t('landing.workflow.configure.manualApiKey'),
        subtext: t('landing.workflow.configure.manualApiKeySubtext')
      }
    ]
  },
  {
    id: 'provision',
    icon: Server,
    title: t('landing.workflow.provision.title'),
    subtitle: t('landing.workflow.provision.subtitle'),
    color: 'from-purple-500 to-purple-600',
    bgColor: 'bg-purple-500/5 dark:bg-purple-500/10',
    borderColor: 'border-purple-200/50 dark:border-purple-800/30',
    details: [
      t('landing.workflow.provision.serverSetup'),
      t('landing.workflow.provision.containerInit'),
      t('landing.workflow.provision.networkConfig')
    ],
    options: [
      {
        icon: Cloud,
        label: t('landing.workflow.provision.managedServer'),
        subtext: t('landing.workflow.provision.managedServerSubtext')
      },
      {
        icon: HardDrive,
        label: t('landing.workflow.provision.ownServer'),
        subtext: t('landing.workflow.provision.ownServerSubtext')
      }
    ]
  },
  {
    id: 'install',
    icon: Terminal,
    title: t('landing.workflow.install.title'),
    subtitle: t('landing.workflow.install.subtitle'),
    color: 'from-emerald-500 to-emerald-600',
    bgColor: 'bg-emerald-500/5 dark:bg-emerald-500/10',
    borderColor: 'border-emerald-200/50 dark:border-emerald-800/30',
    details: [
      t('landing.workflow.install.dependencies'),
      t('landing.workflow.install.gateway'),
      t('landing.workflow.install.services')
    ],
    options: []
  },
  {
    id: 'deploy',
    icon: Rocket,
    title: t('landing.workflow.deploy.title'),
    subtitle: t('landing.workflow.deploy.subtitle'),
    color: 'from-pink-500 to-pink-600',
    bgColor: 'bg-pink-500/5 dark:bg-pink-500/10',
    borderColor: 'border-pink-200/50 dark:border-pink-800/30',
    details: [
      t('landing.workflow.deploy.startServices'),
      t('landing.workflow.deploy.healthCheck'),
      t('landing.workflow.deploy.dnsSetup')
    ],
    options: []
  }
];

export function FlowingWorkflow() {
  const { t } = useTranslation();
  const workflowCards = getWorkflowCards(t);
  const containerRef = useRef(null);
  const isInView = useInView(containerRef, { once: false, margin: '-100px' });
  const [activeCard, setActiveCard] = useState<string | null>(null);
  const [autoPlayIndex, setAutoPlayIndex] = useState(0);

  // Auto-play animation
  useEffect(() => {
    const interval = setInterval(() => {
      setAutoPlayIndex((prev) => (prev + 1) % (workflowCards.length + 1));
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  // Sync activeCard with autoPlayIndex
  useEffect(() => {
    if (autoPlayIndex < workflowCards.length) {
      setActiveCard(workflowCards[autoPlayIndex].id);
    } else {
      setActiveCard('success');
    }
  }, [autoPlayIndex]);

  return (
    <div ref={containerRef} className="relative w-full">
      {/* Workflow Container */}
      <div className="relative py-4 min-h-[500px]">
        {/* Connecting SVG Paths */}
        <svg
          className="absolute inset-0 w-full h-full pointer-events-none"
          style={{ zIndex: 0 }}
        >
          <defs>
            <linearGradient id="pathGradient1" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="rgb(59, 130, 246)" stopOpacity="0.3" />
              <stop offset="100%" stopColor="rgb(168, 85, 247)" stopOpacity="0.3" />
            </linearGradient>
            <linearGradient id="pathGradient2" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="rgb(168, 85, 247)" stopOpacity="0.3" />
              <stop offset="100%" stopColor="rgb(16, 185, 129)" stopOpacity="0.3" />
            </linearGradient>
            <linearGradient id="pathGradient3" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="rgb(16, 185, 129)" stopOpacity="0.3" />
              <stop offset="100%" stopColor="rgb(236, 72, 153)" stopOpacity="0.3" />
            </linearGradient>
          </defs>

          {/* Curved paths between cards */}
          {isInView && (
            <>
              <motion.path
                d="M 200 150 Q 350 100, 500 150"
                stroke="url(#pathGradient1)"
                strokeWidth="3"
                fill="none"
                strokeDasharray="10,5"
                initial={{ pathLength: 0, opacity: 0 }}
                animate={{ pathLength: 1, opacity: 1 }}
                transition={{ duration: 1.5, delay: 0.5 }}
              />
              <motion.path
                d="M 700 150 Q 850 200, 1000 150"
                stroke="url(#pathGradient2)"
                strokeWidth="3"
                fill="none"
                strokeDasharray="10,5"
                initial={{ pathLength: 0, opacity: 0 }}
                animate={{ pathLength: 1, opacity: 1 }}
                transition={{ duration: 1.5, delay: 1 }}
              />
              <motion.path
                d="M 1200 150 Q 1350 100, 1500 150"
                stroke="url(#pathGradient3)"
                strokeWidth="3"
                fill="none"
                strokeDasharray="10,5"
                initial={{ pathLength: 0, opacity: 0 }}
                animate={{ pathLength: 1, opacity: 1 }}
                transition={{ duration: 1.5, delay: 1.5 }}
              />
            </>
          )}
        </svg>

        {/* Cards Grid */}
        <div className="relative grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6 max-w-5xl mx-auto">
          {workflowCards.map((card, idx) => {
            const CardIcon = card.icon;
            const isActive = activeCard === card.id;

            return (
              <motion.div
                key={card.id}
                initial={{ opacity: 0, y: 50, scale: 0.9 }}
                animate={isInView ? { opacity: 1, y: 0, scale: 1 } : {}}
                transition={{
                  duration: 0.6,
                  delay: idx * 0.15,
                  type: 'spring',
                  stiffness: 100
                }}
                onMouseEnter={() => setActiveCard(card.id)}
                onMouseLeave={() => setActiveCard(null)}
                className="relative z-10"
              >
                <motion.div
                  className={`
                    relative rounded-xl border-2 ${card.borderColor} ${card.bgColor}
                    p-4 cursor-pointer transition-all duration-300
                    hover:shadow-2xl min-h-[280px] flex flex-col
                  `}
                  animate={isActive ? {
                    scale: 1.05,
                    y: -8,
                    boxShadow: '0 20px 60px rgba(0,0,0,0.15)'
                  } : {}}
                  whileHover={{ y: -4 }}
                >
                  {/* Floating Badge */}
                  <motion.div
                    className="absolute -top-3 -right-3 w-8 h-8 rounded-full bg-white dark:bg-gray-900 border-2 border-emerald-500 flex items-center justify-center shadow-lg"
                    initial={{ scale: 0 }}
                    animate={isInView ? { scale: 1 } : {}}
                    transition={{ delay: 0.5 + idx * 0.15, type: 'spring' }}
                  >
                    <span className="text-xs font-bold text-emerald-600">{idx + 1}</span>
                  </motion.div>

                  {/* Icon */}
                  <motion.div
                    className={`
                      w-12 h-12 rounded-lg bg-gradient-to-br ${card.color}
                      flex items-center justify-center mb-3 shadow-lg
                    `}
                    animate={isActive ? {
                      rotate: [0, -5, 5, -5, 0],
                      scale: [1, 1.1, 1]
                    } : {}}
                    transition={{ duration: 0.5 }}
                  >
                    <CardIcon className="w-6 h-6 text-white" />
                  </motion.div>

                  {/* Title */}
                  <h3 className="text-lg font-bold mb-1">{card.title}</h3>
                  <p className="text-xs text-muted-foreground mb-3">{card.subtitle}</p>

                  {/* Details */}
                  <div className="space-y-2">
                    {card.details.map((detail, detailIdx) => (
                      <motion.div
                        key={detail}
                        initial={{ opacity: 0, x: -10 }}
                        animate={isActive ? { opacity: 1, x: 0 } : { opacity: 0.6, x: 0 }}
                        transition={{ delay: detailIdx * 0.1 }}
                        className="flex items-center gap-2 text-xs"
                      >
                        <motion.div
                          className="w-1.5 h-1.5 rounded-full bg-emerald-500"
                          animate={isActive ? { scale: [1, 1.5, 1] } : {}}
                          transition={{ duration: 0.3, delay: detailIdx * 0.1 }}
                        />
                        <span className={isActive ? 'text-foreground font-medium' : 'text-muted-foreground'}>
                          {detail}
                        </span>
                      </motion.div>
                    ))}
                  </div>

                  {/* Flexibility Options */}
                  {card.options && card.options.length > 0 && (
                    <div className="mt-auto pt-4 border-t border-border/30 space-y-2 overflow-hidden" style={{ minHeight: '120px' }}>
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={isActive ? { opacity: 1 } : { opacity: 0 }}
                        transition={{ duration: 0.3, delay: 0.2 }}
                        className="space-y-2"
                      >
                      {card.options.map((option, optIdx) => {
                        const OptionIcon = option.icon;
                        return (
                          <motion.div
                            key={optIdx}
                            initial={{ opacity: 0, x: -10 }}
                            animate={isActive ? { opacity: 1, x: 0 } : { opacity: 0, x: -10 }}
                            transition={{ delay: 0.3 + optIdx * 0.1 }}
                            className="flex items-center gap-2 p-2 rounded-lg bg-background/50 border border-border/30 hover:border-emerald-500/50 transition-colors"
                          >
                            <div className={`w-6 h-6 rounded-md bg-gradient-to-br ${card.color} flex items-center justify-center`}>
                              <OptionIcon className="w-3 h-3 text-white" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="text-xs font-semibold text-foreground truncate">
                                {option.label}
                              </div>
                              <div className="text-[10px] text-muted-foreground">
                                {option.subtext}
                              </div>
                            </div>
                          </motion.div>
                        );
                      })}
                      </motion.div>
                    </div>
                  )}

                  {/* Checkmark Animation */}
                  <motion.div
                    className="absolute bottom-4 right-4"
                    initial={{ scale: 0 }}
                    animate={isActive ? { scale: 1 } : { scale: 0 }}
                  >
                    <CheckCircle2 className="w-6 h-6 text-emerald-500" />
                  </motion.div>

                  {/* Glow Effect */}
                  {isActive && (
                    <motion.div
                      className={`absolute inset-0 rounded-2xl bg-gradient-to-br ${card.color} opacity-10 -z-10`}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 0.1 }}
                      transition={{ duration: 0.3 }}
                    />
                  )}
                </motion.div>
              </motion.div>
            );
          })}
        </div>

        {/* Success Card - Centered below */}
        <motion.div
          initial={{ opacity: 0, y: 30, scale: 0.9 }}
          animate={activeCard === 'success' ? { opacity: 1, y: 0, scale: 1 } : { opacity: 0.5, y: 0, scale: 0.95 }}
          transition={{ duration: 0.6, type: 'spring' }}
          className="relative mt-6 max-w-sm mx-auto"
        >
          <motion.div
            className="relative bg-gradient-to-br from-emerald-500/10 via-teal-500/10 to-blue-500/10 border-2 border-emerald-500/30 rounded-xl p-4 overflow-hidden"
            animate={activeCard === 'success' ? {
              boxShadow: [
                '0 0 20px rgba(16, 185, 129, 0.2)',
                '0 0 40px rgba(16, 185, 129, 0.4)',
                '0 0 20px rgba(16, 185, 129, 0.2)',
              ]
            } : {}}
            transition={{ duration: 2, repeat: Infinity }}
          >
            {/* Background Animation */}
            <motion.div
              className="absolute inset-0 bg-gradient-to-r from-emerald-500/5 to-teal-500/5"
              animate={{ opacity: [0.3, 0.6, 0.3] }}
              transition={{ duration: 2, repeat: Infinity }}
            />

            <div className="relative flex items-center gap-4">
              {/* Animated Logo */}
              <motion.div
                className="relative"
                animate={{
                  rotate: 360,
                  scale: [1, 1.1, 1]
                }}
                transition={{
                  rotate: { duration: 20, repeat: Infinity, ease: 'linear' },
                  scale: { duration: 2, repeat: Infinity }
                }}
              >
                <img
                  src="/openclaw.svg"
                  alt="OpenClaw"
                  className="w-12 h-12 drop-shadow-lg"
                />
                {/* Orbiting dots */}
                {[0, 120, 240].map((angle, idx) => (
                  <motion.div
                    key={idx}
                    className="absolute top-1/2 left-1/2 w-2 h-2 bg-emerald-500 rounded-full"
                    animate={{
                      x: [
                        Math.cos((angle * Math.PI) / 180) * 30 - 4,
                        Math.cos(((angle + 360) * Math.PI) / 180) * 30 - 4
                      ],
                      y: [
                        Math.sin((angle * Math.PI) / 180) * 30 - 4,
                        Math.sin(((angle + 360) * Math.PI) / 180) * 30 - 4
                      ]
                    }}
                    transition={{
                      duration: 3,
                      repeat: Infinity,
                      delay: idx * 0.3,
                      ease: 'linear'
                    }}
                  />
                ))}
              </motion.div>

              <div className="flex-1">
                <motion.div
                  className="flex items-center gap-1.5 text-base font-bold text-emerald-600 mb-1"
                  initial={{ opacity: 0, x: -10 }}
                  animate={isInView ? { opacity: 1, x: 0 } : {}}
                  transition={{ delay: 1 }}
                >
                  <Globe className="w-4 h-4" />
                  <span>{t('landing.workflow.success.title')}</span>
                  <motion.div
                    animate={{ scale: [1, 1.2, 1] }}
                    transition={{ duration: 1, repeat: Infinity }}
                  >
                    <Sparkles className="w-4 h-4 text-yellow-500" />
                  </motion.div>
                </motion.div>
                <motion.div
                  className="text-xs text-muted-foreground mb-2"
                  initial={{ opacity: 0, x: -10 }}
                  animate={isInView ? { opacity: 1, x: 0 } : {}}
                  transition={{ delay: 1.1 }}
                >
                  {t('landing.workflow.success.description')}
                </motion.div>
                <motion.div
                  className="inline-flex items-center gap-1.5 bg-emerald-500 text-white px-2.5 py-1 rounded-lg text-xs font-semibold"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={isInView ? { opacity: 1, scale: 1 } : {}}
                  transition={{ delay: 1.2 }}
                >
                  <Clock className="w-3 h-3" />
                  <span>{t('landing.workflow.success.time')}</span>
                </motion.div>
              </div>

              {/* Live Indicator */}
              <motion.div
                className="flex items-center gap-1.5 text-xs font-semibold"
                initial={{ opacity: 0 }}
                animate={isInView ? { opacity: 1 } : {}}
                transition={{ delay: 1.3 }}
              >
                <motion.div
                  className="w-2 h-2 bg-green-500 rounded-full"
                  animate={{ opacity: [1, 0.3, 1] }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                />
                <span className="text-green-600">{t('landing.workflow.success.live')}</span>
              </motion.div>
            </div>
          </motion.div>
        </motion.div>
      </div>

      {/* Floating particles */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {[...Array(8)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-2 h-2 bg-emerald-500/30 rounded-full"
            style={{
              left: `${10 + i * 12}%`,
              top: `${20 + (i % 3) * 30}%`
            }}
            animate={{
              y: [0, -30, 0],
              opacity: [0, 1, 0],
              scale: [0, 1, 0]
            }}
            transition={{
              duration: 3 + i * 0.5,
              repeat: Infinity,
              delay: i * 0.3
            }}
          />
        ))}
      </div>
    </div>
  );
}
